import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import Button from '@/components/CustomButtons/Button';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import useFormValidation from '@/hooks/useFormValidation';
import loader from '@/assets/img/loader.svg';
import { WellsFargoSettingsApi } from '@/api/clients/wells-fargo-settings/wells-fargo-settings.api';
import { SftpConnectionsApi } from '@/api/clients/sftp-connections/sftp-connections.api';
import WellsFargoApiCredentialSection from './WellsFargoApiCredentialSection';
import WellsFargoWebhookSubscriptionSection from './WellsFargoWebhookSubscriptionSection';
import { useTranslation } from 'react-i18next';

const WellsFargoSettingsTab = ({ organizationId, isManaged }) => {
  const { t } = useTranslation();
  const nextReconId = useRef(0);
  const createReconItem = useCallback(
    (value = '') => ({ id: nextReconId.current++, value }),
    [],
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [sftpId, setSftpId] = useState('');
  const [outboundPath, setOutboundPath] = useState('');
  const [inboundAck, setInboundAck] = useState('');
  const [pmPrefix, setPmPrefix] = useState('');
  const [ackPrefix, setAckPrefix] = useState('');
  const [notificationPrefix, setNotificationPrefix] = useState('');
  const [reconPrefix, setReconPrefix] = useState('');
  const [inboundRecon, setInboundRecon] = useState([]);
  const [sftpConnections, setSftpConnections] = useState([]);

  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation();

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, connectionsRes] = await Promise.all([
        WellsFargoSettingsApi.getSettings(organizationId),
        SftpConnectionsApi.getSftpConnections(organizationId),
      ]);
      const data = settingsRes.data;
      setEnabled(data.enable_wells_fargo ?? false);
      setSftpId(
        data.wells_fargo_sftp_id != null
          ? String(data.wells_fargo_sftp_id)
          : '',
      );
      setOutboundPath(data.wells_fargo_outbound_path ?? '');
      setInboundAck(data.wells_fargo_inbound_ack ?? '');
      setPmPrefix(data.wells_fargo_pm_prefix ?? '');
      setAckPrefix(data.wells_fargo_ack_prefix ?? '');
      setNotificationPrefix(data.wells_fargo_notification_prefix ?? '');
      setReconPrefix(data.wells_fargo_recon_prefix ?? '');
      setInboundRecon(
        (data.wells_fargo_inbound_recon ?? []).map((v) => createReconItem(v)),
      );
      setSftpConnections(connectionsRes.data?.data ?? []);
    } catch (error) {
      console.error('Failed to load Wells Fargo settings', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId, createReconItem]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const clearReconErrors = useCallback(() => {
    Object.keys(fieldErrors).forEach((key) => {
      if (key.startsWith('wells_fargo_inbound_recon[')) {
        clearFieldError(key);
      }
    });
  }, [fieldErrors, clearFieldError]);

  const handleAddReconFolder = () => {
    setInboundRecon((prev) => [...prev, createReconItem()]);
  };

  const handleReconFolderChange = (index, value) => {
    setInboundRecon((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], value };
      return updated;
    });
    clearFieldError(`wells_fargo_inbound_recon[${index}]`);
  };

  const handleRemoveReconFolder = (index) => {
    setInboundRecon((prev) => prev.filter((_, i) => i !== index));
    clearReconErrors();
  };

  const handleUpdate = async () => {
    if (isManaged) return;
    clearAllErrors();
    setSaving(true);

    const payload = {
      enable_wells_fargo: enabled,
      wells_fargo_sftp_id: enabled ? Number(sftpId) || null : null,
      wells_fargo_outbound_path: enabled ? outboundPath : null,
      wells_fargo_inbound_ack: enabled ? inboundAck : null,
      wells_fargo_pm_prefix: enabled ? pmPrefix : null,
      wells_fargo_ack_prefix: enabled ? ackPrefix : null,
      wells_fargo_notification_prefix: enabled ? notificationPrefix : null,
      wells_fargo_recon_prefix: enabled ? reconPrefix : null,
      wells_fargo_inbound_recon: enabled
        ? inboundRecon.map((item) => item.value)
        : [],
    };

    try {
      const res = await WellsFargoSettingsApi.updateSettings(
        organizationId,
        payload,
      );
      const data = res.data;
      setEnabled(data.enable_wells_fargo ?? false);
      setSftpId(
        data.wells_fargo_sftp_id != null
          ? String(data.wells_fargo_sftp_id)
          : '',
      );
      setOutboundPath(data.wells_fargo_outbound_path ?? '');
      setInboundAck(data.wells_fargo_inbound_ack ?? '');
      setPmPrefix(data.wells_fargo_pm_prefix ?? '');
      setAckPrefix(data.wells_fargo_ack_prefix ?? '');
      setNotificationPrefix(data.wells_fargo_notification_prefix ?? '');
      setReconPrefix(data.wells_fargo_recon_prefix ?? '');
      setInboundRecon(
        (data.wells_fargo_inbound_recon ?? []).map((v) => createReconItem(v)),
      );
      toast.success('Wells Fargo settings updated successfully');
    } catch (error) {
      if (error.response?.status === 400) {
        setBackendErrors(error.response?.data);
      } else {
        toast.error('Failed to update Wells Fargo settings');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='loaders' style={{ marginTop: '20px' }}>
        <img alt='loader' src={loader} />
      </div>
    );
  }

  return (
    <>
      <FormErrorSummary errors={generalErrors} />
      {isManaged && (
        <Alert severity='info' sx={{ mt: 3 }}>
          {t('organizations.update.membership.wellsReadOnly')}
        </Alert>
      )}

      <GridContainer style={{ paddingTop: '30px' }}>
        <GridItem xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked);
                  clearFieldError('enable_wells_fargo');
                }}
                name='enable_wells_fargo'
                color='primary'
                disabled={isManaged}
              />
            }
            label='Enable Wells Fargo'
            sx={{
              my: 2,
              '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <div className='custom-select'>
            <FormControl
              variant='standard'
              fullWidth
              error={!!fieldErrors.wells_fargo_sftp_id}>
              <InputLabel id='wells-fargo-sftp-id-label'>
                SFTP Connection
              </InputLabel>
              <Select
                labelId='wells-fargo-sftp-id-label'
                id='wells_fargo_sftp_id'
                name='wells_fargo_sftp_id'
                value={sftpId}
                onChange={(e) => {
                  setSftpId(e.target.value);
                  clearFieldError('wells_fargo_sftp_id');
                }}
                className='full-width-elem'
                disabled={isManaged}>
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>
                {sftpConnections.map((conn) => (
                  <MenuItem key={conn.id} value={String(conn.id)}>
                    {conn.alias_name} ({conn.host})
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.wells_fargo_sftp_id && (
                <FormHelperText>
                  {fieldErrors.wells_fargo_sftp_id}
                </FormHelperText>
              )}
            </FormControl>
          </div>
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Outbound Path (e.g. /outbound/)'
            id='wells_fargo_outbound_path'
            name='wells_fargo_outbound_path'
            onChange={(e) => {
              setOutboundPath(e.target.value);
              clearFieldError('wells_fargo_outbound_path');
            }}
            value={outboundPath}
            type='text'
            disabled={isManaged}
            formControlProps={{
              fullWidth: true,
            }}
            error={!!fieldErrors.wells_fargo_outbound_path}
            inputProps={{
              helperText: fieldErrors.wells_fargo_outbound_path,
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Inbound Acknowledgment Path (e.g. /inbound/ack/)'
            id='wells_fargo_inbound_ack'
            name='wells_fargo_inbound_ack'
            onChange={(e) => {
              setInboundAck(e.target.value);
              clearFieldError('wells_fargo_inbound_ack');
            }}
            value={inboundAck}
            type='text'
            disabled={isManaged}
            formControlProps={{
              fullWidth: true,
            }}
            error={!!fieldErrors.wells_fargo_inbound_ack}
            inputProps={{
              helperText: fieldErrors.wells_fargo_inbound_ack,
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={10}>
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant='subtitle1'>File Prefixes</Typography>
          </Box>
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='PM Prefix'
            id='wells_fargo_pm_prefix'
            name='wells_fargo_pm_prefix'
            onChange={(e) => {
              setPmPrefix(e.target.value);
              clearFieldError('wells_fargo_pm_prefix');
            }}
            value={pmPrefix}
            type='text'
            disabled={isManaged}
            formControlProps={{
              fullWidth: true,
            }}
            error={!!fieldErrors.wells_fargo_pm_prefix}
            inputProps={{
              helperText: fieldErrors.wells_fargo_pm_prefix,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='ACK Prefix'
            id='wells_fargo_ack_prefix'
            name='wells_fargo_ack_prefix'
            onChange={(e) => {
              setAckPrefix(e.target.value);
              clearFieldError('wells_fargo_ack_prefix');
            }}
            value={ackPrefix}
            type='text'
            disabled={isManaged}
            formControlProps={{
              fullWidth: true,
            }}
            error={!!fieldErrors.wells_fargo_ack_prefix}
            inputProps={{
              helperText: fieldErrors.wells_fargo_ack_prefix,
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Notification Prefix'
            id='wells_fargo_notification_prefix'
            name='wells_fargo_notification_prefix'
            onChange={(e) => {
              setNotificationPrefix(e.target.value);
              clearFieldError('wells_fargo_notification_prefix');
            }}
            value={notificationPrefix}
            type='text'
            disabled={isManaged}
            formControlProps={{
              fullWidth: true,
            }}
            error={!!fieldErrors.wells_fargo_notification_prefix}
            inputProps={{
              helperText: fieldErrors.wells_fargo_notification_prefix,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Recon Prefix'
            id='wells_fargo_recon_prefix'
            name='wells_fargo_recon_prefix'
            onChange={(e) => {
              setReconPrefix(e.target.value);
              clearFieldError('wells_fargo_recon_prefix');
            }}
            value={reconPrefix}
            type='text'
            disabled={isManaged}
            formControlProps={{
              fullWidth: true,
            }}
            error={!!fieldErrors.wells_fargo_recon_prefix}
            inputProps={{
              helperText: fieldErrors.wells_fargo_recon_prefix,
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={10}>
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant='subtitle1' sx={{ flexGrow: 1 }}>
                Inbound Recon Folders
              </Typography>
              <Button
                variant='outlined'
                size='sm'
                disabled={isManaged}
                onClick={handleAddReconFolder}>
                <AddIcon sx={{ mr: 0.5 }} /> Add Folder
              </Button>
            </Box>
            {fieldErrors.wells_fargo_inbound_recon && (
              <Typography variant='caption' color='error' sx={{ mb: 1 }}>
                {fieldErrors.wells_fargo_inbound_recon}
              </Typography>
            )}
            {inboundRecon.map((item, index) => (
              <Box
                key={item.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CustomInput
                  labelText={`Recon Folder ${index + 1} (e.g. /inbound/recon/)`}
                  id={`wells_fargo_inbound_recon_${item.id}`}
                  name={`wells_fargo_inbound_recon[${index}]`}
                  onChange={(e) =>
                    handleReconFolderChange(index, e.target.value)
                  }
                  value={item.value}
                  type='text'
                  disabled={isManaged}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  error={!!fieldErrors[`wells_fargo_inbound_recon[${index}]`]}
                  inputProps={{
                    helperText:
                      fieldErrors[`wells_fargo_inbound_recon[${index}]`],
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveReconFolder(index)}
                  color='error'
                  disabled={isManaged}
                  size='small'>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <Button
            color='primary'
            onClick={handleUpdate}
            disabled={saving || isManaged}>
            {saving ? 'Saving...' : 'Update'}
          </Button>
        </GridItem>
      </GridContainer>

      <WellsFargoApiCredentialSection organizationId={organizationId} />
      <WellsFargoWebhookSubscriptionSection organizationId={organizationId} />
    </>
  );
};

export default WellsFargoSettingsTab;
